import { useAppDispatch, useAppSelector } from "@/app/hooks";
import * as symbolJsonUtils from "@arcgis/core/symbols/support/jsonUtils";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils";
import { ActionIcon, Box, Divider, Group, Image, Modal, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { displayLegendLayer, setDisplayLegendLayer } from "../pqMapSlice";

interface LegendEntry {
    title: string;
    /** SDK-rendered preview of the symbol. */
    symbolHtml: string;
}

/** Shows the legend of the layer chosen in the layer list. */
const LegendModal: React.FC<{ onNotice: (message: string) => void }> = ({ onNotice }) => {
    const dispatch = useAppDispatch();
    const layer = useAppSelector(displayLegendLayer);
    const [entries, setEntries] = useState<LegendEntry[] | null>(null);
    const [opened, { open, close }] = useDisclosure();

    useEffect(() => {
        if (!layer) return;
        let active = true;
        const createLegend = async () => {
            if (layer.legendInfos) {
                const symbols = await Promise.all(layer.legendInfos.map((info) => symbolUtils.renderPreviewHTML(symbolJsonUtils.fromJSON(info.symbol), { size: 20 })));
                if (!active) return;
                setEntries(layer.legendInfos.flatMap((info, i) => symbols[i] ? [{ title: info.title, symbolHtml: symbols[i].outerHTML }] : []));
            }
            if (active) open();
        };
        void createLegend().catch(() => { if (active) onNotice('The layer legend could not load. Please try again.'); });
        return () => { active = false; };
    }, [layer, onNotice, open]);

    const onClose = () => {
        dispatch(setDisplayLegendLayer(null));
        close();
    };

    return (
        <Modal styles={{ body: { backgroundColor: '#E8E8E8', padding: 0, border: '3px solid #B3B3B3' }, overlay: { zIndex: 300 }, inner: { zIndex: 300 } }} opened={opened} onClose={onClose} withCloseButton={false} centered p={0}>
            <ActionIcon style={{ position: 'fixed', top: '10px', right: '10px' }} onClick={onClose} size='xs' color="pq-red" aria-label='Close legend'>
                <IconX />
            </ActionIcon>
            {layer && (
                <Stack gap={0}>
                    <Group justify='start' gap={0}>
                        <Box>
                            {layer.imgSrc && <Image w={60} h={75} src={layer.imgSrc} alt={layer.title} />}
                        </Box>
                        <Divider color="pq-med-gray" orientation='vertical' size={3} />
                        <Text fw={600} ml={10}>{layer.title}</Text>
                    </Group>
                    <Divider color="pq-med-gray" size={3} />
                    <Stack m={10} align="start" justify="start" gap={10}>
                        {entries?.map((entry, i) => (
                            <Group key={i}>
                                <Box dangerouslySetInnerHTML={{ __html: entry.symbolHtml }} />
                                <Text>{entry.title}</Text>
                            </Group>
                        ))}
                    </Stack>
                </Stack>
            )}
        </Modal>
    );
}

export default LegendModal;
